import { useState } from 'react'
import { Button } from '@w/ui/components/button'
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from '@w/ui/components/select'
import { SettingsIcon } from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from '@w/ui/components/popover'
import { Tabs, TabsList, TabsPanel, TabsTab } from '@w/ui/components/tabs'
import type { CoreViewApi } from '../view-api'
import { indexViewBlocks } from '../../doc-blocks'
import type { PageBlock } from '../../doc-blocks'
import { useBlockViewServices } from './services'

type QuestionDefaults = { id: string; qid: string; importance?: 'low' | 'medium' | 'high'; difficulty?: 'low' | 'medium' | 'high'; weight?: number }
function isQuestionPart(part: PageBlock): part is PageBlock & QuestionDefaults {
  const rating = (value: unknown) => value === undefined || value === 'low' || value === 'medium' || value === 'high'
  return (part.type === 'question' || part.type === 'cloze') && typeof part.qid === 'string'
    && rating(part.importance) && rating(part.difficulty) && (part.weight === undefined || typeof part.weight === 'number')
}
/**
 * A question's or cloze's settings (flashcards-editor-home.requirements.md 2026-11-02): a popover that opens on Current
 * (the learner's own card values, from the host) with Defaults (the authored values) as the second tab.
 */
export function QuestionSettings({ block, api, compact = false, ...state }: {
  block: QuestionDefaults
  api: CoreViewApi
  /** An inline cloze shows only the icon, so the sentence keeps its flow. */
  compact?: boolean
  /** A question card also shows its explanation field while its settings are open. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const { updateBlock, renderQuestionSettings } = useBlockViewServices()
  const [own, setOwn] = useState(false)
  const settings = state.open ?? own
  const setSettings = state.onOpenChange ?? setOwn
  const qid = block.qid
  // Every part edits the first document-order part's tuple (editor-home.spec.md:55).
  let defaults = block
  for (const { block: part } of indexViewBlocks(api.state.doc.toJSON()).values()) {
    if (isQuestionPart(part) && part.qid === qid) {
      defaults = part
      break
    }
  }
  const patch = (value: Record<string, unknown>) => { updateBlock(defaults.id, value) }
  const current = renderQuestionSettings?.(qid)
  const summary = <span className="text-on-surface-secondary truncate text-xs font-normal capitalize">{api.t(`contentBlocksEditor.document.${defaults.importance ?? 'medium'}`)} · {api.t(`contentBlocksEditor.document.${defaults.difficulty ?? 'medium'}`)} · {`${defaults.weight ?? 1}×`}</span>
  const defaultsPanel = <div data-testid={`question-settings-panel-${qid}`} className="flex flex-wrap items-end gap-5 pt-3">
    {(['importance', 'difficulty'] as const).map(field => <div key={field} className="flex min-w-24 flex-col">
      <span className="text-on-surface-secondary text-xs capitalize">{field}</span>
      <Select minimal value={defaults[field] ?? 'medium'} onValueChange={value => { if (value) patch({ [field]: value }) }}>
        <SelectTrigger aria-label={field} data-testid={`question-${field}-${qid}`}><SelectValue className="capitalize" /></SelectTrigger>
        <SelectPopup align="start">{(['low', 'medium', 'high'] as const).map(value => <SelectItem key={value} data-testid={`question-${field}-${qid}-${value}`} value={value}>{api.t(`contentBlocksEditor.document.${value}`)}</SelectItem>)}</SelectPopup>
      </Select></div>)}
    <div className="flex items-center gap-1"><span>Weight</span>
      <Button size="sm" variant="text" data-testid={`question-weight-decrease-${qid}`} disabled={(defaults.weight ?? 1) <= 0.5}
        onClick={() => { patch({ weight: Math.max(0.5, (defaults.weight ?? 1) - 0.5) }) }}>−</Button>
      <span data-testid={`question-weight-${qid}`}>{defaults.weight ?? 1}×</span>
      <Button size="sm" variant="text" data-testid={`question-weight-increase-${qid}`} disabled={(defaults.weight ?? 1) >= 5}
        onClick={() => { patch({ weight: Math.min(5, (defaults.weight ?? 1) + 0.5) }) }}>+</Button>
    </div>
  </div>
  const label = api.t('contentBlocksEditor.document.settings')
  return <Popover open={settings} onOpenChange={setSettings}>
    <PopoverTrigger render={compact
      ? <IconButton data-testid={`question-settings-${qid}`} className="mx-0.5 size-5 align-middle" size="sm" variant="text" semanticRole="secondary" />
      : <Button data-testid={`question-settings-${qid}`} className="-ml-2 max-w-full justify-start" size="sm" variant="text" semanticRole="secondary" />}>
      <SettingsIcon />{compact ? label : <span className="flex min-w-0 items-center gap-2"><span className="shrink-0">{label}</span>{summary}</span>}
    </PopoverTrigger>
    <PopoverPopup align="start" className="w-96"><div className="grid gap-3"><PopoverTitle>{label}</PopoverTitle>
      {current ? <Tabs defaultValue="current"><TabsList><TabsTab value="current" data-testid={`question-settings-current-${qid}`}>Current</TabsTab><TabsTab value="defaults" data-testid={`question-settings-defaults-${qid}`}>Defaults</TabsTab></TabsList>
        <TabsPanel value="current">{current}</TabsPanel><TabsPanel value="defaults">{defaultsPanel}</TabsPanel></Tabs> : defaultsPanel}
    </div></PopoverPopup>
  </Popover>
}
